import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from '@lingui/core/macro';

import Person from '@rover/icons/svg/person.svg';
import { Box, Button } from '@rover/kibble/core';
import { SM_MIN } from '@rover/kibble/styles/MediaQuery';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import { selectors as searchFiltersSelectors } from '@rover/react-lib/src/pages/search/SearchPage/SearchFilters.duck';
import useDucks from '@rover/shared/js/hooks/useDucks';

import { useSearchByNameExperiment } from './hooks/useSearchByNameExperiment';
import { SEARCH_BY_NAME_OPEN_PARAM } from './constants';
import SearchByNameOverlay from './SearchByNameOverlay';

const SearchByNameButton = (): JSX.Element | null => {
  const { isEnabled } = useSearchByNameExperiment();
  const isMobile = !useMatchMedia(`(min-width:${SM_MIN}px)`);
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  // Set by both open paths (button + deep-link) so the overlay can label the open event.
  const [openMethod, setOpenMethod] = useState<'button' | 'deeplink'>('button');
  const { data } = useDucks({
    selector: (state) => ({
      searchLocation: searchFiltersSelectors.getFilters(state).location ?? '',
    }),
  });

  const stripOpenParam = useCallback((): void => {
    const params = new URLSearchParams(location.search);
    if (!params.has(SEARCH_BY_NAME_OPEN_PARAM)) return;
    params.delete(SEARCH_BY_NAME_OPEN_PARAM);
    const nextSearch = params.toString();
    // replace (not push) so the stale param doesn't survive the back button.
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true }
    );
  }, [navigate, location.hash, location.pathname, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasOpenParam = params.get(SEARCH_BY_NAME_OPEN_PARAM) === 'true';

    if (!isEnabled) {
      if (hasOpenParam) stripOpenParam();
      return;
    }

    // Param preserved while open: SearchPage suppresses the search filters modal while isSearchByNameRequested is true. Stripped on close.
    // Guard !isOpen so a button-open isn't relabeled 'deeplink' when this effect re-runs on the param change.
    if (hasOpenParam && !isOpen) {
      setOpenMethod('deeplink');
      setIsOpen(true);
    }
  }, [isEnabled, isOpen, location.search, stripOpenParam]);

  const handleOpen = useCallback((): void => {
    const params = new URLSearchParams(location.search);
    params.set(SEARCH_BY_NAME_OPEN_PARAM, 'true');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true }
    );
    setOpenMethod('button');
    setIsOpen(true);
  }, [navigate, location.hash, location.pathname, location.search]);

  const handleClose = useCallback((): void => {
    setIsOpen(false);
    stripOpenParam();
  }, [stripOpenParam]);

  if (!isEnabled || isMobile) return null;

  return (
    <>
      <Box flexShrink="0">
        <Button
          variant="default"
          icon={Person}
          onPress={handleOpen}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          testID="search-by-name-button"
        >
          {t`Search by name`}
        </Button>
      </Box>
      <SearchByNameOverlay
        isOpen={isOpen}
        onClose={handleClose}
        initialLocation={data.searchLocation}
        openMethod={openMethod}
      />
    </>
  );
};

export default SearchByNameButton;
